import React, { useState, useEffect, useId } from 'react';
import axios from 'axios';
import { GOLD, NAVY_FORM } from './colors';
import { FORM_BACK_BTN, FORM_SAVE_BTN, FORM_SUBMIT_BTN } from './theme';
import { LoadError, useIsNarrow, gridCols } from './ui';

const API = '/api';
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

const DEMO_S1 = {
  last_working_date: '2026-05-30',
  notice_period_served: '1 month',
  reason_for_leaving: 'resignation',
  garden_leave: false,
  knowledge_transfer_completed: true,
  tasks_reassigned_to: 'Operations Team',
  notes: 'Smooth transition expected.'
};

const DEMO_S2 = {
  disable_pc_account: true,
  disable_mailbox: true,
  archive_mailbox: true,
  mailbox_close_date: '2026-06-01',
  mailbox_auto_reply: 'This mailbox is no longer monitored. Please contact operations@enkocapital.com.',
  mailbox_access_users: [],
  email_forward_to: 'operations@enkocapital.com',
  archive_personal_data: true,
  revoke_azure_sharepoint: true,
  revoke_subscriptions: '',
  external_subscriptions: ['Bloomberg', 'LSEG Workspace'],
};

const DEMO_S3 = {
  final_pay_date: '2026-05-31',
  accrued_holiday_days: 5,
  p45_issued: true,
  pension_deregistered: true,
  health_insurance_cancelled: true,
  signatory_lists_updated: true,
  org_chart_updated: true,
  ddq_rfp_updated: true,
  has_outstanding_loans: false,
  loans_details: '',
  loans_cleared: false,
  has_guarantees: false,
  guarantees_details: '',
  guarantees_cleared: false
};

const RequiredMark = () => {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '2px' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      <span style={{ color: '#e74c3c', cursor: 'default' }}>*</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)', backgroundColor: NAVY_FORM, color: '#fff',
          padding: '5px 10px', borderRadius: '4px', fontSize: '12px',
          whiteSpace: 'nowrap', zIndex: 200, pointerEvents: 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
        }}>
          This field is required.
          <span style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)', width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: `5px solid ${NAVY_FORM}`
          }} />
        </span>
      )}
    </span>
  );
};

/* BENEFITTASK: the proof-upload affordance beside each Benefits checkbox. Presentational only -
   the parent owns the request, so the two checkboxes share one upload path. */
const BenefitProof = ({ taskType, label, done, busy, onUpload }) => (
  <div style={{ margin: '-4px 0 8px 26px', fontSize: '12px', color: '#666' }}>
    <label style={{ cursor: busy ? 'default' : 'pointer', color: GOLD, fontWeight: 600 }}>
      <input
        type="file"
        style={{ display: 'none' }}
        disabled={busy}
        aria-label={`Upload ${label} proof`}
        onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(taskType, f); e.target.value = ''; }}
      />
      {busy ? 'Uploading...' : done ? 'Replace proof' : `Upload ${label} proof`}
    </label>
    {done && <span style={{ marginLeft: '8px', color: '#1e8449' }}>Done</span>}
  </div>
);

const Spinner = () => (
  <span style={{
    display: 'inline-block', width: '13px', height: '13px',
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#fff', borderRadius: '50%',
    animation: 'spin 0.65s linear infinite',
    marginRight: '8px', verticalAlign: 'middle'
  }} />
);

// S3SPLIT: `allowedSections` and `payrollScopeOnly` are OPT-IN. Both default to today's exact
// behaviour (all three sections, all field groups), so the COO Dashboard, EmployeeProfile and
// the staff views that mount this form are completely unaffected. Only FinanceView passes them.
// These are a usability and honesty fix on top of the real enforcement, which is in the backend:
// Sections 1/2 already 403 for payroll, and S3SPLIT restricts Section 3 field writes server side.
function LeaverForm({ employeeId, employeeName, requiresVisa, location, section, completedBy,
                      onBack, onNavigate, onComplete,
                      allowedSections = [1, 2, 3], payrollScopeOnly = false,
                      // HRPAYFIX point 9: the MIRROR IMAGE of payrollScopeOnly. That flag keeps
                      // Section 3's payroll groups and hides the Admin/Loans/Guarantees ones,
                      // for finance and payroll. This one does the opposite, for hr: keep the
                      // admin groups, hide Final Pay and Benefits, because the server now
                      // refuses hr those six fields. Opt-in and defaulting to false, so every
                      // existing mount is byte-identical. The real enforcement is server side;
                      // this stops HR being shown inputs whose save would 403.
                      adminScopeOnly = false }) {
  // RESPONSIVE Phase 2: two-column field rows (styles.row) reflow to one column below ~900px.
  const isNarrow = useIsNarrow();
  const rowStyle = { ...styles.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  // Never let `section` land on a section this caller may not see.
  const _initialSection = allowedSections.includes(section) ? section : allowedSections[0];
  const [activeSection, setActiveSection] = useState(_initialSection || 1);
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState(null);
  const [employees, setEmployees] = useState([]);

  // LOADERR: the initial form/employee-list load failed (never a 404 - see the fetch below).
  // LOADERR3B: split into TWO flags. One flag for two unrelated fetches meant a failure of the
  // employee list rendered a banner claiming the SAVED FORM had failed - which is how a routine
  // 403 on GET /employees/ came to read as "Couldn't load the saved form data" while the form
  // had in fact loaded perfectly. Each fetch now owns its own flag and its own accurate wording.
  const [loadFailed, setLoadFailed] = useState(false);
  const [employeeListFailed, setEmployeeListFailed] = useState(false);
  // BENEFITTASK: which benefit proof is uploading, and any failure message.
  const [benefitUploading, setBenefitUploading] = useState(null);
  const [benefitError, setBenefitError] = useState(null);
  // EXTSUBS: the configurable provider list, fetched from the backend. Defaults to the current 8
  // so the group renders correctly even before the fetch resolves or if it fails.
  const [subscriptionProviders, setSubscriptionProviders] = useState([
    'Bloomberg', 'LSEG Workspace', 'Arcfina', 'Research', 'Adobe', 'CRM', 'GitHub', 'DigitalOcean',
  ]);
  const [s1, setS1] = useState({
    last_working_date: '', notice_period_served: '', reason_for_leaving: 'resignation',
    garden_leave: false, garden_leave_start: '', garden_leave_end: '',
    garden_leave_working: null,
    asset_laptop: false, asset_desktop: false, asset_screens: false,
    asset_mobile: false, asset_access_cards: false,
    knowledge_transfer_completed: false, tasks_reassigned_to: '', notes: ''
  });

  const [s2, setS2] = useState({
    disable_pc_account: false, disable_mailbox: false, archive_mailbox: false,
    mailbox_close_date: '', mailbox_auto_reply: '',
    mailbox_access_users: [],
    email_forward_to: '', archive_personal_data: false,
    revoke_azure_sharepoint: false, revoke_subscriptions: '',
    external_subscriptions: []
  });
  const [mailboxEmailInput, setMailboxEmailInput] = useState('');

  const [s3, setS3] = useState({
    final_pay_date: '', accrued_holiday_days: '',
    p45_issued: false, p45_file: null,
    pension_deregistered: false, health_insurance_cancelled: false,
    signatory_lists_updated: false, org_chart_updated: false, ddq_rfp_updated: false,
    has_outstanding_loans: false, loans_details: '', loans_cleared: false,
    has_guarantees: false, guarantees_details: '', guarantees_cleared: false
  });

  const populateFromData = (d) => {
    setS1({
      last_working_date: d.last_working_date || '',
      notice_period_served: d.notice_period_served || '',
      reason_for_leaving: d.reason_for_leaving || 'resignation',
      garden_leave: d.garden_leave ?? false,
      garden_leave_start: d.garden_leave_start || '',
      garden_leave_end: d.garden_leave_end || '',
      garden_leave_working: d.garden_leave_working ?? null,
      // ASSETREC: restore from the saved form. These were hardcoded to false because the backend
      // never returned them (it never stored them either); now that it does, reading the saved
      // value is what makes the group actually reload.
      asset_laptop: d.asset_laptop ?? false,
      asset_desktop: d.asset_desktop ?? false,
      asset_screens: d.asset_screens ?? false,
      asset_mobile: d.asset_mobile ?? false,
      asset_access_cards: d.asset_access_cards ?? false,
      knowledge_transfer_completed: d.knowledge_transfer_completed ?? false,
      tasks_reassigned_to: d.tasks_reassigned_to || '',
      notes: d.notes || ''
    });
    setS2(prev => ({
      ...prev,
      disable_pc_account: d.disable_pc_account ?? false,
      disable_mailbox: d.disable_mailbox ?? false,
      archive_mailbox: d.archive_mailbox ?? false,
      // MAILBOXDATE: now persisted server-side; restore them like every other saved field.
      mailbox_close_date: d.mailbox_close_date || '',
      mailbox_auto_reply: d.mailbox_auto_reply || '',
      mailbox_access_users: d.mailbox_access_users
        ? d.mailbox_access_users.split(', ').filter(Boolean)
        : [],
      email_forward_to: d.email_forward_to || '',
      // FIELDAUDIT2 LOW: these two checkboxes are written server-side but were never returned or
      // restored, so they always reloaded unticked over a stored True. Now returned by the
      // response and restored here.
      archive_personal_data: d.archive_personal_data ?? false,
      revoke_azure_sharepoint: d.revoke_azure_sharepoint ?? false,
      // EXTSUBS: restore the selected subscriptions. Stored comma-separated (same shape as
      // mailbox_access_users above), split back to the array the checkboxes bind to. Before this
      // the field was never returned or restored, so ticks always reloaded empty.
      external_subscriptions: d.external_subscriptions
        ? d.external_subscriptions.split(',').map(s => s.trim()).filter(Boolean)
        : [],
    }));
    setS3(prev => ({
      ...prev,
      final_pay_date: d.final_pay_date || '',
      accrued_holiday_days: d.accrued_holiday_days ?? '',
      p45_issued: d.p45_issued ?? false,
      // S3SPLIT: these two were never restored because the client used the wrong names
      // (pension_notified / benefits_to_terminate), which the backend silently dropped.
      pension_deregistered: d.pension_deregistered ?? false,
      health_insurance_cancelled: d.health_insurance_cancelled ?? false,
      signatory_lists_updated: d.signatory_lists_updated ?? false,
      org_chart_updated: d.org_chart_updated ?? false,
      ddq_rfp_updated: d.ddq_rfp_updated ?? false,
      has_outstanding_loans: d.has_outstanding_loans ?? false,
      loans_details: d.loans_details || '',
      loans_cleared: d.loans_cleared ?? false,
      has_guarantees: d.has_guarantees ?? false,
      guarantees_details: d.guarantees_details || '',
      guarantees_cleared: d.guarantees_cleared ?? false,
    }));
  };

  useEffect(() => {
    axios.get(`${API}/leaver/${employeeId}`)
      .then(res => {
        setFormData(res.data);
        populateFromData(res.data);
      })
      // LOADERR: a 404 is "no form yet" (normal fresh start); anything else means the user
      // would be editing a BLANK form believing it is the saved state - surface it.
      .catch((err) => { if (err?.response?.status !== 404) setLoadFailed(true); });
    // LOADERR3B: the employee list exists ONLY to populate Section 1's handover dropdown, so it
    // is fetched only when Section 1 is actually reachable. Payroll (allowedSections=[3]) cannot
    // see Section 1 and is 403 on GET /employees/ by FINPAYROLL's design, so making the call at
    // all was both pointless and the source of a misleading error banner. This changes no access
    // control - it stops asking for something this caller neither needs nor is allowed to have.
    if (allowedSections.includes(1)) {
      axios.get(`${API}/employees/`)
        .then(res => setEmployees(res.data.filter(e => e.id !== employeeId && e.is_active)))
        // LOADERR: without this list the Section 1 handover dropdown is silently empty.
        .catch(() => setEmployeeListFailed(true));
    }
    // EXTSUBS: fetch the configurable provider list. On failure the state keeps its default 8, so
    // the group still renders - a stale list is far better than a broken form, and this is not
    // per-employee data.
    axios.get(`${API}/leaver/subscription-providers`)
      .then(res => { if (Array.isArray(res.data?.providers) && res.data.providers.length) setSubscriptionProviders(res.data.providers); })
      .catch(() => {});
  }, [employeeId]); // eslint-disable-line

  const allComplete = Boolean(
    formData?.section1_completed &&
    formData?.section2_completed &&
    formData?.section3_completed
  );

  const goToSection = (n) => {
    // S3SPLIT: the single choke point for section changes - the progress bar, the auto-advance
    // after a submit, and any future caller all route through here. Previously it set any
    // section unconditionally, which is how a payroll account could open Manager and IT.
    if (!allowedSections.includes(n)) return;
    setActiveSection(n);
    setError(null);
    setSuccess(null);
    if (onNavigate) onNavigate(n);
  };

  // BENEFITTASK: upload proof for one benefit task. The server ticks the checkbox column and
  // derives the task status from it, so the response IS the new truth - repopulating from it
  // keeps the form in step without a second round trip or any local guess about the new state.
  const uploadBenefitProof = async (taskType, file) => {
    setBenefitUploading(taskType);
    setBenefitError(null);
    try {
      const fd = new FormData();
      fd.append('proof_file', file);
      const res = await axios.post(`${API}/leaver/${employeeId}/benefit-proof/${taskType}`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(res.data);
      populateFromData(res.data);
    } catch (err) {
      setBenefitError(formatError(err.response?.data?.detail) || 'Could not upload the proof document.');
    }
    setBenefitUploading(null);
  };

  const sanitize = (obj) => Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, v === '' ? null : v])
  );

  const formatError = (detail) => {
    if (!detail) return 'Unexpected error';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg).join(', ');
    return JSON.stringify(detail);
  };

  const buildPayload = () => {
    if (activeSection === 1) return sanitize({ ...s1, completed_by: completedBy });
    if (activeSection === 2) return sanitize({
      ...s2,
      mailbox_access_users: s2.mailbox_access_users.join(', '),
      external_subscriptions: s2.external_subscriptions.join(', '),
      completed_by: completedBy
    });
    if (activeSection === 3) {
      const { p45_file, ...s3rest } = s3;   // p45_file is uploaded separately (multipart), not part of the JSON body
      return sanitize({
        ...s3rest,
        accrued_holiday_days: parseInt(s3.accrued_holiday_days) || 0,
        completed_by: completedBy
      });
    }
  };

  // Upload the P45 file (Section 3) if one was selected. Optional - no-op otherwise.
  const uploadP45IfPresent = async () => {
    if (activeSection !== 3 || !s3.p45_file) return;
    const fd = new FormData();
    fd.append('p45_file', s3.p45_file);
    const res = await axios.post(`${API}/leaver/${employeeId}/p45`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setFormData(res.data);
    setS3(prev => ({ ...prev, p45_file: null }));
  };

  // Discard a staged (not-yet-definitive) P45 upload - the × button shown while the file
  // is only in Temp/ (section 3 not yet finally submitted).
  const clearStagedP45 = async () => {
    try {
      await axios.delete(`${API}/leaver/${employeeId}/p45/temp`);
      const freshRes = await axios.get(`${API}/leaver/${employeeId}`);
      setFormData(freshRes.data);
    } catch {
      setError('Could not clear the staged file. Please try again.');
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.post(`${API}/leaver/${employeeId}/section${activeSection}?draft=true`, buildPayload());
      await uploadP45IfPresent();
      setSuccess('Draft saved.');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Error saving draft');
    }
    setSaving(false);
  };

  const submit = async () => {
    setLoading(true);
    setError(null);

    if (activeSection === 2) {
      // MAILBOXDATE: closing or archiving the mailbox needs a date to act on. Mirrors the
      // server-side 400 in submit_section2; drafts are unaffected (saveDraft skips submit()).
      if ((s2.disable_mailbox || s2.archive_mailbox) && !s2.mailbox_close_date) {
        setError("'Close mailbox on' is required when the mailbox is being disabled or archived.");
        setLoading(false); return;
      }
    }

    if (activeSection === 3) {
      // ADMINDELAY: last working date is required before Section 3 can be completed - the Admin
      // leaver items dispatch ON that date. Mirrors the server-side 400 in submit_section3.
      if (!(formData?.last_working_date || s1.last_working_date)) {
        setError('Last working date is missing - fill it in Section 1 (AO/Manager) before completing Section 3.');
        setLoading(false); return;
      }
      if (s3.has_outstanding_loans && !s3.loans_cleared) {
        setError('Outstanding loans must be cleared or confirmed before submitting.');
        setLoading(false); return;
      }
      if (s3.has_guarantees && !s3.guarantees_cleared) {
        setError('Outstanding guarantees must be addressed before submitting.');
        setLoading(false); return;
      }
    }

    try {
      await axios.post(`${API}/leaver/${employeeId}/section${activeSection}`, buildPayload());
      await uploadP45IfPresent();

      // Refresh to get updated completion flags
      const freshRes = await axios.get(`${API}/leaver/${employeeId}`);
      const d = freshRes.data;
      setFormData(d);

      const nowAllDone = d.section1_completed && d.section2_completed &&
                         d.section3_completed;

      if (nowAllDone) {
        setSuccess('Leaver form complete. All notifications and tasks have been sent automatically.');
        setTimeout(() => onComplete(), 2000);
      } else {
        setSuccess('Section submitted successfully!');
        // Auto-advance to the first incomplete section
        // HRLEAVER: among the sections this viewer may open, so hr (Sections 1 and 3) moves on to
        // Section 3 instead of stopping at the IT section it cannot open.
        const next = [1, 2, 3].filter(n => allowedSections.includes(n)).find(n => !d[`section${n}_completed`]);
        if (next) setTimeout(() => { setSuccess(null); goToSection(next); }, 800);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Error submitting form');
    }
    setLoading(false);
  };

  const sectionLabels = { 1: 'Manager', 2: 'IT', 3: 'Payroll & Benefits' };
  const sectionOwners = { 1: 'COO', 2: 'COO', 3: 'COO' };

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.header}>
        <h1 style={styles.title}>Leaver Form</h1>
        {/* LOADERR: the saved form could not be loaded - editing would happen on blank data
            believing it is the saved state. LOADERR3B: this banner now covers ONLY that fetch. */}
        {loadFailed && (
          <div style={{ margin: '8px 0' }}>
            <LoadError message="Couldn't load the saved form data - reload before editing." />
          </div>
        )}
        {/* LOADERR3B: a separate, accurately worded banner for the handover list. Its failure
            leaves the Section 1 dropdown empty; it says nothing about the saved form, which in
            this case loaded fine. */}
        {employeeListFailed && (
          <div style={{ margin: '8px 0' }}>
            <LoadError message="Couldn't load the employee list - the handover dropdown in Section 1 will be empty." />
          </div>
        )}
        <p style={styles.subtitle}>{employeeName}</p>
      </div>

      {/* Progress - every section this caller is allowed to see is clickable. S3SPLIT: sections
          outside allowedSections are not rendered at all, so payroll never sees a Manager or IT
          step it cannot open. */}
      <div style={styles.progress}>
        {[1, 2, 3].filter(n => allowedSections.includes(n)).map(n => {
          const isCompleted = formData?.[`section${n}_completed`];
          const isCurrent = activeSection === n;
          return (
            <div key={n}
              style={{ ...styles.progressItem, cursor: !isCurrent ? 'pointer' : 'default' }}
              onClick={() => !isCurrent && goToSection(n)}
              title={!isCurrent ? `Go to ${sectionLabels[n]}` : undefined}>
              <div style={{
                ...styles.progressDot,
                backgroundColor: isCurrent ? NAVY_FORM : isCompleted ? '#27ae60' : '#ddd',
              }}>
                {!isCurrent && isCompleted ? '✓' : n}
              </div>
              <div>
                <p style={{ ...styles.progressLabel, color: isCurrent ? NAVY_FORM : isCompleted ? '#555' : '#999' }}>
                  {sectionLabels[n]}
                </p>
                <p style={styles.progressOwner}>{sectionOwners[n]}</p>
              </div>
            </div>
          );
        })}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.successMsg}>{success}</div>}

      {/* ─── SECTION 1 - Manager ─── */}
      {activeSection === 1 && allowedSections.includes(1) && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Section 1: Manager</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {formData?.section1_completed && <span style={styles.submittedBadge}>✓ Submitted</span>}
              {!allComplete && isTestAccount() && <button style={styles.demoBtn} onClick={() => setS1(DEMO_S1)}>Fill demo data</button>}
            </div>
          </div>

          {allComplete && (
            <div style={styles.readOnlyBanner}>
              All sections have been submitted. This form is now read-only.
            </div>
          )}

          <div style={allComplete ? { pointerEvents: 'none', opacity: 0.72 } : {}}>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Departure Details</h3>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-last-working-date`}>Last Working Date <RequiredMark /></label>
                
                <input id={`${a11yId}-last-working-date`} style={styles.input} type="date" value={s1.last_working_date}
                  onChange={e => setS1({...s1, last_working_date: e.target.value})} />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-notice-period-served`}>Notice Period Served</label>
                
                <input id={`${a11yId}-notice-period-served`} style={styles.input} value={s1.notice_period_served}
                  onChange={e => setS1({...s1, notice_period_served: e.target.value})}
                  placeholder="e.g. 1 month" />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-reason-for-leaving`}>Reason for Leaving</label>
              
              <select id={`${a11yId}-reason-for-leaving`} style={styles.input} value={s1.reason_for_leaving}
                onChange={e => setS1({...s1, reason_for_leaving: e.target.value})}>
                <option value="resignation">Resignation</option>
                <option value="redundancy">Redundancy</option>
                <option value="end_of_contract">End of contract</option>
                <option value="dismissal">Dismissal</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Garden Leave</h3>
            <label style={styles.checkboxItem}>
              <input type="checkbox" checked={s1.garden_leave}
                onChange={e => setS1({...s1, garden_leave: e.target.checked})} />
              Garden leave applies
            </label>
            {s1.garden_leave && (
              <>
                <div style={{...styles.row, marginTop: '12px'}}>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-start-date`}>Start Date</label>
                    
                    <input id={`${a11yId}-start-date`} style={styles.input} type="date" value={s1.garden_leave_start}
                      onChange={e => setS1({...s1, garden_leave_start: e.target.value})} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label} htmlFor={`${a11yId}-end-date`}>End Date</label>
                    
                    <input id={`${a11yId}-end-date`} style={styles.input} type="date" value={s1.garden_leave_end}
                      onChange={e => setS1({...s1, garden_leave_end: e.target.value})} />
                  </div>
                </div>
                <div style={{...styles.field, marginTop: '12px'}}>
                  <label style={styles.label}>Working status during garden leave</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radio}>
                      <input type="radio" name="garden_leave_working" checked={s1.garden_leave_working === true}
                        onChange={() => setS1({...s1, garden_leave_working: true})} />
                      Working during garden leave
                    </label>
                    <label style={styles.radio}>
                      <input type="radio" name="garden_leave_working" checked={s1.garden_leave_working === false}
                        onChange={() => setS1({...s1, garden_leave_working: false})} />
                      Not working during garden leave
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Asset Recovery</h3>
            <div style={styles.checkboxGroup}>
              {[
                ['asset_laptop', 'Laptop computer'],
                ['asset_desktop', 'Desktop computer'],
                ['asset_screens', 'Screens and peripherals'],
                ['asset_mobile', 'Mobile phone (if applicable)'],
                ['asset_access_cards', 'Access cards'],
              ].map(([key, label]) => (
                <label key={key} style={styles.checkboxItem}>
                  <input type="checkbox" checked={s1[key]}
                    onChange={e => setS1({...s1, [key]: e.target.checked})} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Handover</h3>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-assign-transition-responsibility-to`}>Assign transition responsibility to</label>
              
              <select id={`${a11yId}-assign-transition-responsibility-to`} style={styles.input} value={s1.tasks_reassigned_to}
                onChange={e => setS1({...s1, tasks_reassigned_to: e.target.value})}>
                <option value="">Select an employee</option>
                {employees.map(emp => (
                  <option key={emp.id} value={`${emp.first_name} ${emp.last_name}`}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
              <p style={styles.hint}>This person will be assigned a handover task when the form is fully submitted.</p>
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-notes`}>Notes</label>
              
              <textarea id={`${a11yId}-notes`} style={styles.textarea} rows={2} value={s1.notes}
                onChange={e => setS1({...s1, notes: e.target.value})}
                placeholder="Any additional notes" />
            </div>
          </div>
          </div>

          {!allComplete && (
            <div style={styles.btnRow}>
              {onBack && (
                <button
                  style={styles.backBtnRow}
                  onClick={onBack}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
                >
                  ← Back to dashboard
                </button>
              )}
              <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                {saving ? 'Saving...' : 'Save draft'}
              </button>
              <button style={styles.submitBtn} onClick={submit} disabled={loading || saving}>
                {loading ? <><Spinner />Submitting...</> : 'Submit'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── SECTION 2 - IT ─── */}
      {activeSection === 2 && allowedSections.includes(2) && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Section 2: IT</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {formData?.section2_completed && <span style={styles.submittedBadge}>✓ Submitted</span>}
              {!allComplete && isTestAccount() && <button style={styles.demoBtn} onClick={() => setS2(DEMO_S2)}>Fill demo data</button>}
            </div>
          </div>

          {allComplete && (
            <div style={styles.readOnlyBanner}>
              All sections have been submitted. This form is now read-only.
            </div>
          )}

          <div style={allComplete ? { pointerEvents: 'none', opacity: 0.72 } : {}}>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Account & Access</h3>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.disable_pc_account}
                  onChange={e => setS2({...s2, disable_pc_account: e.target.checked})} />
                Disable user PC account
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.revoke_azure_sharepoint}
                  onChange={e => setS2({...s2, revoke_azure_sharepoint: e.target.checked})} />
                Revoke Azure / SharePoint access
              </label>
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>External Subscriptions</h3>
            {/* SUBSTASK: text updated - subscriptions are no longer bundled into the RFA IT email;
                each selected provider now gets its own cancellation Task assigned to that provider's
                configured contact when Section 2 is submitted. */}
            <p style={styles.hint}>When Section 2 is submitted, a cancellation task is created for each selected subscription, assigned to that provider's responsible contact.</p>
            <div style={{...styles.checkboxGroup, marginTop: '12px'}}>
              {/* EXTSUBS: the provider list is fetched from the backend (GET
                  /leaver/subscription-providers), driven by LEAVER_SUBSCRIPTION_PROVIDERS, so
                  adding a provider is an .env change with NO edit to this file. */}
              {subscriptionProviders.map(sub => (
                <label key={sub} style={styles.checkboxItem}>
                  <input type="checkbox"
                    checked={s2.external_subscriptions.includes(sub)}
                    onChange={e => {
                      const updated = e.target.checked
                        ? [...s2.external_subscriptions, sub]
                        : s2.external_subscriptions.filter(s => s !== sub);
                      setS2({...s2, external_subscriptions: updated});
                    }} />
                  {sub}
                </label>
              ))}
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Mailbox</h3>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.disable_mailbox}
                  onChange={e => setS2({...s2, disable_mailbox: e.target.checked})} />
                Disable user mailbox
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.archive_mailbox}
                  onChange={e => setS2({...s2, archive_mailbox: e.target.checked})} />
                Archive user mailbox
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s2.archive_personal_data}
                  onChange={e => setS2({...s2, archive_personal_data: e.target.checked})} />
                Archive personal data
              </label>
            </div>
            <div style={{...styles.field, marginTop: '12px'}}>
              <label style={styles.label} htmlFor={`${a11yId}-close-mailbox-on`}>Close mailbox on</label>
              
              <input id={`${a11yId}-close-mailbox-on`} style={styles.input} type="date" value={s2.mailbox_close_date}
                onChange={e => setS2({...s2, mailbox_close_date: e.target.value})} />
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-auto-reply-message-for-rejected-emai`}>Auto-reply message for rejected emails</label>
              
              <textarea id={`${a11yId}-auto-reply-message-for-rejected-emai`} style={styles.textarea} rows={3} value={s2.mailbox_auto_reply}
                onChange={e => setS2({...s2, mailbox_auto_reply: e.target.value})}
                placeholder="e.g. This mailbox is no longer monitored. Please contact operations@enkocapital.com." />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Mailbox access granted to</label>
              <div style={styles.emailTagContainer}>
                {s2.mailbox_access_users.map(email => (
                  <span key={email} style={styles.emailTag}>
                    {email}
                    <button style={styles.emailTagRemove}
                      onClick={() => setS2({...s2, mailbox_access_users: s2.mailbox_access_users.filter(e => e !== email)})}>
                      ×
                    </button>
                  </span>
                ))}
                <input
                  style={styles.emailTagInput}
                  value={mailboxEmailInput}
                  onChange={e => setMailboxEmailInput(e.target.value)}
                  onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ',') && mailboxEmailInput.trim()) {
                      e.preventDefault();
                      const val = mailboxEmailInput.trim().replace(/,$/, '');
                      if (val && !s2.mailbox_access_users.includes(val)) {
                        setS2({...s2, mailbox_access_users: [...s2.mailbox_access_users, val]});
                      }
                      setMailboxEmailInput('');
                    }
                  }}
                  placeholder={s2.mailbox_access_users.length === 0 ? 'Type email and press Enter' : 'Add another email…'}
                />
              </div>
              <p style={styles.hint}>Press Enter or comma to add each address.</p>
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor={`${a11yId}-email-forwarding-to`}>Email forwarding to</label>
              
              <input id={`${a11yId}-email-forwarding-to`} style={styles.input} value={s2.email_forward_to}
                onChange={e => setS2({...s2, email_forward_to: e.target.value})}
                placeholder="e.g. operations@enkocapital.com" />
            </div>
          </div>
          </div>

          {!allComplete && (
            <div style={styles.btnRow}>
              {onBack && (
                <button
                  style={styles.backBtnRow}
                  onClick={onBack}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
                >
                  ← Back to dashboard
                </button>
              )}
              <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                {saving ? 'Saving...' : 'Save draft'}
              </button>
              <button style={styles.submitBtn} onClick={submit} disabled={loading || saving}>
                {loading ? <><Spinner />Submitting...</> : 'Submit'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── SECTION 3 - HR ─── */}
      {activeSection === 3 && allowedSections.includes(3) && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>Section 3: HR & Payroll</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {formData?.section3_completed && <span style={styles.submittedBadge}>✓ Submitted</span>}
              {!allComplete && isTestAccount() && <button style={styles.demoBtn} onClick={() => setS3(DEMO_S3)}>Fill demo data</button>}
            </div>
          </div>

          {allComplete && (
            <div style={styles.readOnlyBanner}>
              All sections have been submitted. This form is now read-only.
            </div>
          )}

          <div style={allComplete ? { pointerEvents: 'none', opacity: 0.72 } : {}}>
          <div style={styles.finalPayWarning}>
            Final paycheck cannot be processed until equipment is recovered, transition is complete, and compliance is signed off.
          </div>

          {/* HRPAYFIX point 9: "Part 1 - Payroll". Hidden for hr, which the server now refuses
              these six fields. The mirror of the payrollScopeOnly guard below, which hides
              "Part 2 - Admin" from finance and payroll. Between the two, each role is shown the
              half of Section 3 it may actually save. */}
          {!adminScopeOnly && (
          <>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Final Pay</h3>
            <div style={rowStyle}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-final-pay-date`}>Final Pay Date</label>
                
                <input id={`${a11yId}-final-pay-date`} style={styles.input} type="date" value={s3.final_pay_date}
                  onChange={e => setS3({...s3, final_pay_date: e.target.value})} />
              </div>
              <div style={styles.field}>
                <label style={styles.label} htmlFor={`${a11yId}-accrued-holiday-days-to-pay`}>Accrued Holiday Days to Pay</label>
                
                <input id={`${a11yId}-accrued-holiday-days-to-pay`} style={styles.input} type="number" value={s3.accrued_holiday_days}
                  onChange={e => setS3({...s3, accrued_holiday_days: e.target.value})}
                  placeholder="e.g. 5" min="0" />
              </div>
            </div>
            {location === 'UK' && (
              <>
                <label style={styles.checkboxItem}>
                  <input type="checkbox" checked={s3.p45_issued}
                    onChange={e => setS3({...s3, p45_issued: e.target.checked})} />
                  P45 issued
                </label>
                {s3.p45_issued && (
                  <div style={{...styles.field, marginTop: '10px'}}>
                    <label style={styles.label} htmlFor={`${a11yId}-upload-p45-optional`}>Upload P45 (optional)</label>
                    
                    <input id={`${a11yId}-upload-p45-optional`} type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => setS3({...s3, p45_file: e.target.files[0] || null})}
                      style={styles.fileInput} />
                    {s3.p45_file
                      ? <p style={styles.hint}>Selected: {s3.p45_file.name}</p>
                      : formData?.p45_file_name
                      ? <p style={styles.hint}>Uploaded: {formData.p45_file_name}</p>
                      : formData?.p45_temp_url && (
                        <p style={{ ...styles.hint, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          Staged: {formData.p45_temp_url.split('/').pop()}
                          <button
                            type="button"
                            onClick={clearStagedP45}
                            style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}
                            title="Remove staged file"
                          >×</button>
                        </p>
                      )}
                  </div>
                )}
              </>
            )}
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Benefits</h3>
            <div style={styles.checkboxGroup}>
              {/* BENEFITTASK: each of these two is now backed by a real Task assigned to the
                  entity's payroll contact. The checkbox stays the quick-glance indicator and
                  remains the single source of truth - uploading proof ticks it server side and
                  derives the task status from it, so both routes converge on the same state. */}
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s3.pension_deregistered}
                  aria-label="Pension notified"
                  onChange={e => setS3({...s3, pension_deregistered: e.target.checked})} />
                Pension notified
              </label>
              <BenefitProof
                taskType="leaver_pension_notify"
                label="pension notification"
                done={s3.pension_deregistered}
                busy={benefitUploading === 'leaver_pension_notify'}
                onUpload={uploadBenefitProof}
              />
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s3.health_insurance_cancelled}
                  aria-label="Benefits to terminate"
                  onChange={e => setS3({...s3, health_insurance_cancelled: e.target.checked})} />
                Benefits to terminate
              </label>
              <BenefitProof
                taskType="leaver_benefits_terminate"
                label="benefits termination"
                done={s3.health_insurance_cancelled}
                busy={benefitUploading === 'leaver_benefits_terminate'}
                onUpload={uploadBenefitProof}
              />
            </div>
            {benefitError && (
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#c0392b' }}>{benefitError}</p>
            )}
          </div>
          </>
          )}

          {/* S3SPLIT: "Part 2 - Admin". Rendered only for coo/admin/hr. A payroll or finance
              viewer sees Final Pay and Benefits ("Part 1") and stops there. The backend refuses
              these field writes for those roles regardless, so this is the honest UI on top of
              the real enforcement, not the enforcement itself. */}
          {!payrollScopeOnly && (
          <>
          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Admin</h3>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s3.signatory_lists_updated}
                  onChange={e => setS3({...s3, signatory_lists_updated: e.target.checked})} />
                Authorised signatory
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s3.org_chart_updated}
                  onChange={e => setS3({...s3, org_chart_updated: e.target.checked})} />
                Org chart & website
              </label>
              <label style={styles.checkboxItem}>
                <input type="checkbox" checked={s3.ddq_rfp_updated}
                  onChange={e => setS3({...s3, ddq_rfp_updated: e.target.checked})} />
                DDQ and RFP
              </label>
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Outstanding Loans</h3>
            <label style={styles.checkboxItem}>
              <input type="checkbox" checked={s3.has_outstanding_loans}
                onChange={e => setS3({...s3, has_outstanding_loans: e.target.checked, loans_cleared: false})} />
              Employee has outstanding loans with the company
            </label>
            {s3.has_outstanding_loans && (
              <>
                <div style={{...styles.field, marginTop: '12px'}}>
                  <label style={styles.label} htmlFor={`${a11yId}-details`}>Details</label>
                  
                  <textarea id={`${a11yId}-details`} style={styles.textarea} rows={2} value={s3.loans_details}
                    onChange={e => setS3({...s3, loans_details: e.target.value})}
                    placeholder="Amount, purpose, repayment terms..." />
                </div>
                <div style={styles.loanWarning}>
                  <p style={styles.loanWarningText}><span style={{ fontWeight: 700 }}>Warning: </span>Final paycheck cannot be issued until loans are cleared or a repayment agreement is in place.</p>
                  <label style={styles.checkboxItem}>
                    <input type="checkbox" checked={s3.loans_cleared}
                      onChange={e => setS3({...s3, loans_cleared: e.target.checked})} />
                    Loans cleared / repayment agreement confirmed
                  </label>
                </div>
              </>
            )}
          </div>

          <div style={styles.fieldGroup}>
            <h3 style={styles.groupTitle}>Guarantees</h3>
            <label style={styles.checkboxItem}>
              <input type="checkbox" checked={s3.has_guarantees}
                onChange={e => setS3({...s3, has_guarantees: e.target.checked, guarantees_cleared: false})} />
              Employee is a guarantor or has active guarantees
            </label>
            {s3.has_guarantees && (
              <>
                <div style={{...styles.field, marginTop: '12px'}}>
                  <label style={styles.label} htmlFor={`${a11yId}-details-2`}>Details</label>
                  
                  <textarea id={`${a11yId}-details-2`} style={styles.textarea} rows={2} value={s3.guarantees_details}
                    onChange={e => setS3({...s3, guarantees_details: e.target.value})}
                    placeholder="Nature of guarantee, counterparty, expiry..." />
                </div>
                <div style={styles.loanWarning}>
                  <p style={styles.loanWarningText}><span style={{ fontWeight: 700 }}>Warning: </span>Guarantees must be reviewed and handled before the employee leaves.</p>
                  <label style={styles.checkboxItem}>
                    <input type="checkbox" checked={s3.guarantees_cleared}
                      onChange={e => setS3({...s3, guarantees_cleared: e.target.checked})} />
                    Guarantees addressed and legal sign-off obtained
                  </label>
                </div>
              </>
            )}
          </div>
          </>
          )}
          </div>

          {!allComplete && (
            <div style={styles.btnRow}>
              {onBack && (
                <button
                  style={styles.backBtnRow}
                  onClick={onBack}
                  onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.color = NAVY_FORM; }}
                >
                  ← Back to dashboard
                </button>
              )}
              <button style={styles.saveBtn} onClick={saveDraft} disabled={saving || loading}>
                {saving ? 'Saving...' : 'Save draft'}
              </button>
              <button style={styles.submitBtn} onClick={submit} disabled={loading || saving}>
                {loading ? <><Spinner />Submitting...</> : 'Complete Leaver Form'}
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

const styles = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '800px', margin: '0 auto', padding: '24px', backgroundColor: '#f8f9fa' },
  backBtn: { background: 'none', border: '1px solid rgba(26,39,68,0.25)', color: NAVY_FORM, fontSize: '14px', cursor: 'pointer', padding: '9px 18px', borderRadius: '6px', marginBottom: '24px', fontWeight: '600' },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  subtitle: { fontSize: '16px', color: '#666', margin: '4px 0 0 0' },
  progress: { display: 'flex', gap: '24px', marginBottom: '32px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flexWrap: 'wrap' },
  progressItem: { display: 'flex', alignItems: 'center', gap: '10px' },
  progressDot: { width: '32px', height: '32px', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', flexShrink: 0 },
  progressLabel: { fontSize: '13px', fontWeight: '500', margin: '0 0 2px 0' },
  progressOwner: { fontSize: '11px', color: '#aaa', margin: '0' },
  form: { backgroundColor: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  formHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  formTitle: { fontSize: '18px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  demoBtn: { backgroundColor: '#f0f4ff', color: NAVY_FORM, border: '1px solid #c7d4f5', borderRadius: '6px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  submittedBadge: { backgroundColor: '#edfaf1', color: '#27ae60', border: '1px solid #27ae60', borderRadius: '20px', padding: '4px 14px', fontSize: '13px', fontWeight: '600' },
  readOnlyBanner: { background: '#fffbea', border: '1px solid #d4ac0d', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', color: '#7d6608', fontSize: '14px' },
  fieldGroup: { marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #eee' },
  groupTitle: { fontSize: '14px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 16px 0' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#333', marginBottom: '6px' },
  hint: { fontSize: '12px', color: '#888', margin: '0 0 12px 0' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  textarea: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', resize: 'vertical' },
  checkboxGroup: { display: 'flex', flexDirection: 'column', gap: '12px' },
  checkboxItem: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer', color: '#333' },
  // FORMBTNSHARE: shared action-row trio (see theme.js), identical to NewStarterForm + SponsorForm.
  // backBtnRow is the row-context Back sized to match Save/Submit (the page-header backBtn above,
  // which carries a marginBottom for header use, is left defined for any header usage).
  submitBtn: { ...FORM_SUBMIT_BTN },
  saveBtn: { ...FORM_SAVE_BTN },
  backBtnRow: { ...FORM_BACK_BTN },
  btnRow: { display: 'flex', gap: '12px', marginTop: '8px' },
  finalPayWarning: { backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '6px', padding: '12px 16px', marginBottom: '24px', fontSize: '13px', color: '#c0392b', fontWeight: '500' },
  loanWarning: { backgroundColor: '#fffbf0', border: '1px solid #f39c12', borderRadius: '6px', padding: '12px 16px', marginTop: '12px' },
  loanWarningText: { fontSize: '13px', color: '#b7770d', margin: '0 0 10px 0', fontWeight: '500' },
  fileInput: { fontSize: '13px', color: '#333', marginTop: '4px' },
  taskInfo: { backgroundColor: '#f0f4ff', border: '1px solid #c7d4f5', borderRadius: '6px', padding: '14px 16px', marginBottom: '24px' },
  taskInfoTitle: { fontSize: '13px', fontWeight: '600', color: NAVY_FORM, margin: '0 0 8px 0' },
  taskInfoList: { margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#444', lineHeight: '1.8' },
  error: { backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '6px', padding: '12px 16px', fontSize: '14px', color: '#c0392b', marginBottom: '16px' },
  successMsg: { backgroundColor: '#e8f8f0', border: '1px solid #27ae60', borderRadius: '6px', padding: '12px 16px', fontSize: '14px', color: '#1a7a45', marginBottom: '16px' },
  radioGroup: { display: 'flex', gap: '24px', flexWrap: 'wrap' },
  radio: { fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  emailTagContainer: { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', minHeight: '42px', backgroundColor: '#fff' },
  emailTag: { display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#e8f0fe', color: NAVY_FORM, borderRadius: '12px', padding: '3px 10px', fontSize: '13px', fontWeight: '500' },
  emailTagRemove: { background: 'none', border: 'none', cursor: 'pointer', color: '#555', fontSize: '15px', lineHeight: 1, padding: '0 2px' },
  emailTagInput: { border: 'none', outline: 'none', fontSize: '14px', flexGrow: 1, minWidth: '180px', padding: '2px 4px' }
};

export default LeaverForm;
